import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import React from 'react';

import { PlannerCredentials } from '../../../../domain/credentials/PlannerCredentials';
import { Driver } from '../../../../domain/driver/Driver';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { Project } from '../../../../domain/project/Project';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../react-components/Text';

type BrokenConstraintsPanelProps = {
  brokenConstraints: any;
  type: `drivers` | `stops`;
};

const BrokenConstraintsPanel = ({ type, brokenConstraints }: BrokenConstraintsPanelProps) => {
  return (
    <div style={{ margin: `10px`, fontSize: `14px`, lineHeight: `1.6em` }}>
      <Text tagName="p" intlId={`${type}.brokenconstraints.info`} style={{ color: RoutalPalette.oldWhite }} />

      {Object.keys(brokenConstraints).map((constraintKey) => {
        if (brokenConstraints[constraintKey]) {
          return (
            <p style={{ marginLeft: `8px` }} key={constraintKey}>
              <Text tagName="span" style={{ color: RoutalPalette.oldWhite }}>
                -{` `}
              </Text>
              <Text
                tagName="span"
                intlId={`${type}.brokenconstraints.${constraintKey}`}
                style={{ color: RoutalPalette.oldWhite }}
              />
            </p>
          );
        }
        return null;
      })}
    </div>
  );
};

/**
 * @description Validates if there are any stop or driver optimization warnings.
 * @param {*} type
 * @param {*} stop
 * @param {*} driver
 * @param {*} planGeoFences
 * @param {*} activeProject
 * @param {*} userCredentials
 */
const getBrokenConstraints = ({
  type,
  stop,
  driver,
  planGeoFences,
  activeProject,
  userCredentials,
}: {
  type: `stop` | `driver`;
  stop?: Stop;
  driver?: Driver;
  planGeoFences: Array<GeoFence>;
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): Record<string, any> => {
  let timeWindows;
  let noRequiresMatch;
  let maxDistance;
  let minDistance;
  let maxTime;
  let minTime;
  let weight;
  let volume;
  let maxServices;
  let timeWindow;
  let invalidStopsGeoFences;
  let maxDeliveryTime;
  let invalidStopGeoFences;
  switch (type) {
    case `stop`:
      // Checks if the stop planned_arrival_time and planned_departure_time are inside the stop time_windows.
      timeWindows =
        driver?.status === `not_started` && activeProject?.constraints.time_windows // Only show the warning if the route is not started
          ? stopTimewindowsAreInvalid(stop)
          : false;

      // Checks if vehicle provides the stop requires.
      if (activeProject?.constraints.provide_requires && stop?.driver_id && driver) {
        const requires = stop?.requires ?? [];
        noRequiresMatch = requires.some((r: any) => !driver.provides?.includes(r));
      }

      // Max possible time between a pickup and its delivery.
      if (
        stop?.pickup?.max_delivery_time !== undefined &&
        activeProject?.constraints.max_delivery_time &&
        stop?.planned_arrival_time !== undefined &&
        stop?.pickup.planned_departure_time !== undefined
      ) {
        maxDeliveryTime =
          stop.pickup.max_delivery_time < stop.planned_arrival_time - stop.pickup.planned_departure_time;
      }

      // Check if the stop geofence is inside driver's geofence list
      if (
        activeProject?.geo_fences_enabled &&
        stop?.location?.isValid() &&
        userCredentials?.subscription?.test(`planner.geofences`)
      ) {
        const planGeoFenceForStopWarning = planGeoFences
          .filter((geo) => geo.enabled)
          .find((planGF) =>
            planGF.isMarkerInsideGeoFence({
              lat: stop.location!.lat!,
              lng: stop.location!.lng!,
            })
          );
        invalidStopGeoFences =
          planGeoFenceForStopWarning && driver && !driver?.geo_fences?.includes(planGeoFenceForStopWarning.id);
      }
      return {
        time_windows: timeWindows,
        no_requires_match: noRequiresMatch,
        max_delivery_time: maxDeliveryTime,
        optimization_warning: stop?.optimization_warning,
        invalidStopGeoFences: invalidStopGeoFences,
      };
    case `driver`: {
      // Checks if the total of stops distances exceeds the driver maximum distance.

      const stops = driver?.stops ?? [];

      const timeOnRoute =
        (driver && (stops[stops.length - 1]?.planned_arrival_time || 0) - (stops[0]?.planned_departure_time || 0)) || 0;
      const distanceOnRoute = stops.reduce(
        (p: any, n: Stop) => (n.distance_to_next_stop ? p + n.distance_to_next_stop : p),
        stops.length > 0 ? stops[0].distance_to_previous_stop ?? 0 : 0
      );

      if (activeProject?.constraints.max_distance) {
        // Checks if the total of stops distances exceeds the driver maximum distance.
        maxDistance =
          driver?.max_distance !== undefined && driver?.max_distance !== null && distanceOnRoute > driver.max_distance;
      }

      if (activeProject?.constraints.min_distance) {
        // Checks if the total of stops distances is lower than the driver minimum distance.
        minDistance =
          driver?.min_distance !== undefined && driver?.min_distance !== null && distanceOnRoute < driver.min_distance;
      }

      if (activeProject?.constraints.max_time) {
        // Checks if the total of stops times exceeds the driver maximum time.
        maxTime = driver?.max_time !== undefined && driver?.max_time !== null && timeOnRoute > driver.max_time;
      }

      if (activeProject?.constraints.min_time) {
        // Checks if the total of stops times is lower than the driver minimum time.
        minTime = driver?.min_time !== undefined && driver?.min_time !== null && timeOnRoute < driver.min_time;
      }

      const hasPickups = (driver?.stops ?? []).some((driverStop: Stop) => !!driverStop.pickup_id);

      if (!hasPickups) {
        if (activeProject?.constraints.weight) {
          // Checks if the total of stops volumes exceeds the driver maximum weight.
          weight =
            driver?.stops.reduce((p: any, n: any) => (n.weight && n.type === `delivery` ? p + n.weight : p), 0) >
            (driver?.max_weight ?? 0);
        }

        if (activeProject?.constraints.volume) {
          // Checks if the total of stops volumes exceeds the driver maximum volume.
          volume =
            driver?.stops.reduce((p: any, n: any) => (n.volume && n.type === `delivery` ? p + n.volume : p), 0) >
            (driver?.max_volume ?? 0);
        }
      } else {
        if (activeProject?.constraints.weight && activeProject?.constraints.weight) {
          // Checks if the total of stops and pickups volumes exceeds the driver maximum weight.
          let currentWeight = 0;
          weight = driver?.stops.some((driverStop: Stop) => {
            currentWeight += (driverStop.type === `delivery` ? -1 : 1) * (driverStop.weight ?? 0);
            return currentWeight > (driver.max_weight ?? 0);
          });
        }

        if (activeProject?.constraints.volume && activeProject?.constraints.volume) {
          // Checks if the total of stops and pickups volumes exceeds the driver maximum volume.
          let currentVolume = 0;
          volume = driver?.stops.some((driverStop: Stop) => {
            currentVolume += (driverStop.type === `delivery` ? -1 : 1) * (driverStop.volume ?? 0);
            return currentVolume > (driver.max_weight ?? 0);
          });
        }
      }

      if (activeProject?.constraints.max_services) {
        // Checks if the driver number of stops exceeds its maximum volume.
        maxServices =
          driver?.max_services !== undefined &&
          driver?.max_services !== null &&
          driver?.stops.filter((s) => s.type === `delivery`).length > driver?.max_services;
      }

      // Checks if the driver planned_arrival_time and planned_departure_time are inside the driver time_window.
      timeWindow = driverTimewindowIsInvalid(driver);

      // Checks if driver has a stop that belongs to a geo fence that driver does not have
      invalidStopsGeoFences = driverStopsInvalidGeoFences({ driver, planGeoFences, activeProject, userCredentials });

      return {
        max_distance: maxDistance,
        min_distance: minDistance,
        max_time: maxTime,
        min_time: minTime,
        weight,
        volume,
        max_services: maxServices,
        time_window: timeWindow,
        invalid_stops_geo_fences: invalidStopsGeoFences,
      };
    }
    default:
      return {};
  }
};

export const hasStopBrokenConstraints = ({
  stop,
  driver,
  planGeoFences,
  activeProject,
  userCredentials,
}: {
  stop?: Stop;
  driver?: Driver;
  planGeoFences: Array<GeoFence>;
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): boolean => {
  const brokenConstraints = getBrokenConstraints({
    type: `stop`,
    stop,
    driver,
    planGeoFences,
    activeProject,
    userCredentials,
  });
  const brokenConstraintsArray = Object.keys(brokenConstraints).map((key: string) => brokenConstraints[key]);
  return brokenConstraintsArray.some((item: boolean) => item === true);
};

export const hasDriverBrokenConstraints = ({
  driver,
  planGeoFences,
  activeProject,
  userCredentials,
}: {
  driver?: Driver;
  planGeoFences: Array<GeoFence>;
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}): boolean => {
  const brokenConstraints = getBrokenConstraints({
    type: `driver`,
    driver,
    planGeoFences,
    activeProject,
    userCredentials,
  });
  const brokenConstraintsArray = Object.keys(brokenConstraints).map((key: string) => brokenConstraints[key]);
  return brokenConstraintsArray.some((item: boolean) => item === true);
};

type BrokenConstraintsContentProps = {
  driver?: Driver;
  stop?: Stop;
  type: `driver` | `stop`;
};

export const BrokenConstraintsContent = ({
  driver,
  stop,
  type,
}: React.PropsWithChildren<BrokenConstraintsContentProps>) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();

  let brokenConstraints;

  switch (type) {
    case `stop`:
      if (
        !stop ||
        !hasStopBrokenConstraints({
          stop,
          driver,
          planGeoFences: projectGeoFences ?? [],
          activeProject,
          userCredentials,
        })
      )
        return null;

      brokenConstraints = getBrokenConstraints({
        type: `stop`,
        stop,
        driver,
        planGeoFences: projectGeoFences ?? [],
        activeProject,
        userCredentials,
      });

      return <BrokenConstraintsPanel type="stops" brokenConstraints={brokenConstraints} />;
    case `driver`:
      if (
        !driver ||
        !hasDriverBrokenConstraints({
          driver,
          planGeoFences: projectGeoFences ?? [],
          activeProject,
          userCredentials,
        })
      )
        return null;

      brokenConstraints = getBrokenConstraints({
        type: `driver`,
        driver,
        planGeoFences: projectGeoFences ?? [],
        activeProject,
        userCredentials,
      });

      return <BrokenConstraintsPanel type="drivers" brokenConstraints={brokenConstraints} />;
    default:
      return null;
  }
};

type TooltipPlacementTypes =
  | `left`
  | `right`
  | `top`
  | `bottom`
  | `topLeft`
  | `topRight`
  | `bottomLeft`
  | `bottomRight`;

type StopBrokenConstraintsTooltipProps = {
  driver?: Driver;
  stop?: Stop;
  tooltipPlacement?: TooltipPlacementTypes;
};

const StopBrokenConstraintsTooltip = ({
  stop,
  driver,
  children,
  tooltipPlacement,
}: React.PropsWithChildren<StopBrokenConstraintsTooltipProps>) => {
  return (
    <RoutalTooltip
      id={`stop-${stop?.id}-broken-constraints`}
      placement={tooltipPlacement}
      title={<BrokenConstraintsContent type="stop" stop={stop} driver={driver} />}
      overlayStyle={{
        maxWidth: `300px`,
      }}
    >
      {children}
    </RoutalTooltip>
  );
};

/**
 * @description Validates if the stop planned_arrival_time and planned_departure_time are inside the stop time_windows.
 * @param {*} stop
 */
export const stopTimewindowsAreInvalid = (stop?: Stop) => {
  return (
    stop?.time_windows &&
    stop?.time_windows.length > 0 &&
    stop?.planned_arrival_time !== undefined &&
    stop?.planned_departure_time !== undefined &&
    !stop?.time_windows.some((timeWindow: TimeWindow) =>
      isValidTimeWindow(timeWindow, [stop?.planned_arrival_time, stop?.planned_departure_time])
    )
  );
};

/**
 * @description Validates if the driver planned_arrival_time and planned_departure_time are inside the stop time_windows.
 * @param {*} driver
 */
export const driverTimewindowIsInvalid = (driver?: Driver) => {
  return (
    driver?.planned_start_time !== undefined &&
    driver?.planned_start_time !== null &&
    driver?.planned_end_time !== undefined &&
    driver?.planned_end_time !== null &&
    driver?.time_window &&
    !isValidTimeWindow(driver?.time_window, [driver.planned_start_time, driver.planned_end_time])
  );
};

/**
 * Checks if driver has a stop that belongs to a geo fence that driver does not have
 */
export const driverStopsInvalidGeoFences = ({
  driver,
  planGeoFences,
  activeProject,
  userCredentials,
}: {
  driver?: Driver;
  planGeoFences: Array<GeoFence>;
  activeProject?: Project;
  userCredentials?: PlannerCredentials;
}) => {
  if (!userCredentials?.subscription?.test(`planner.geofences`)) return false;

  if (!activeProject?.geo_fences_enabled) return false;

  if (!driver?.geo_fences || driver?.geo_fences.length === 0) return false;

  return !driver.stops.every((stop) => {
    if (!stop.location?.isValid()) return false;
    const planGeoFencesEnabled = planGeoFences.filter((geo) => geo.enabled);

    const isInsideDriverGeoFence =
      driver.geo_fences &&
      driver.geo_fences.some((driverGeoFence) => {
        const planGeoFence = planGeoFencesEnabled.find((planGF) => planGF.id === driverGeoFence);
        return (
          planGeoFence &&
          planGeoFence.isMarkerInsideGeoFence({
            lat: stop.location!.lat!,
            lng: stop.location!.lng!,
          })
        );
      });

    const isInsidePlanGeoFence = planGeoFencesEnabled.some((planGF) => {
      return planGF.isMarkerInsideGeoFence({
        lat: stop.location!.lat!,
        lng: stop.location!.lng!,
      });
    });

    return isInsideDriverGeoFence || (!isInsideDriverGeoFence && !isInsidePlanGeoFence);
  });
};

/**
 * @description Validates that a time_window is inside another.
 * @param {*} timeWindow
 * @param {*} realTimeWindow
 */
const isValidTimeWindow = (timeWindow: TimeWindow, realTimeWindow: any) =>
  timeWindow[0] <= realTimeWindow[0] && timeWindow[1] >= realTimeWindow[1];

type RouteBrokenConstraintsTooltipProps = {
  driver?: Driver;
  tooltipPlacement?: TooltipPlacementTypes;
};

const RouteBrokenConstraintsTooltip = ({
  driver,
  children,
  tooltipPlacement,
}: React.PropsWithChildren<RouteBrokenConstraintsTooltipProps>) => {
  return (
    <RoutalTooltip
      id={`driver-${driver?.id}-broken-constraints`}
      placement={tooltipPlacement}
      title={<BrokenConstraintsContent type="driver" driver={driver} />}
      overlayStyle={{
        maxWidth: `300px`,
      }}
    >
      {children}
    </RoutalTooltip>
  );
};

RouteBrokenConstraintsTooltip.defaultProps = {};

type BrokenConstraintsTooltipProps = {
  type: `driver` | `stop`;
  driver?: Driver;
  stop?: Stop;
  tooltipPlacement?: TooltipPlacementTypes;
};

export const BrokenConstraintsTooltip = ({
  type,
  stop,
  driver,
  children,
  tooltipPlacement,
}: React.PropsWithChildren<BrokenConstraintsTooltipProps>) => {
  switch (type) {
    case `driver`:
      return (
        <RouteBrokenConstraintsTooltip tooltipPlacement={tooltipPlacement} driver={driver}>
          {children}
        </RouteBrokenConstraintsTooltip>
      );
    case `stop`:
      return (
        <StopBrokenConstraintsTooltip tooltipPlacement={tooltipPlacement} driver={driver} stop={stop}>
          {children}
        </StopBrokenConstraintsTooltip>
      );
    default:
      return null;
  }
};
